import {useCallback, useEffect, useRef, useState} from "react";
import {getResponseBody, getResponseStatus, handleApiError, handleExpiredEntryLinkError, post} from "./api";
import {showMessage} from "./main";

export const expiredTanResentMessage = "Ihr Code war bereits abgelaufen. Wir haben Ihnen einen neuen gesendet.";

const reportError = (error) => {
    if (!handleExpiredEntryLinkError(error)) {
        handleApiError(error);
    }
};

export const useTanConfirmation = ({path, enabled, submitting = false, initialTan = ""}) => {
    const [signatureCode, setSignatureCode] = useState(initialTan);
    const [tanStatus, setTanStatus] = useState(null);
    const [preparing, setPreparing] = useState(false);
    const lifecycle = useRef({active: false});

    useEffect(() => {
        setSignatureCode(initialTan);
    }, [path, initialTan]);

    const refresh = useCallback(async (resend = false) => {
        const current = lifecycle.current;
        if (!current.active || current.pending) return;
        current.pending = true;
        setPreparing(true);
        const isCurrent = () => current.active && current === lifecycle.current;
        try {
            if (resend) {
                await post(`${path}/requestNewTan`, null, false);
                if (!isCurrent()) return;
                setSignatureCode("");
                showMessage("Neuer Code wurde erfolgreich versendet.");
            }
            const status = await post(`${path}/ensureTan`, null, false);
            if (isCurrent()) {
                setTanStatus(status);
                if (status.renewed) {
                    setSignatureCode("");
                    showMessage(expiredTanResentMessage);
                }
            }
            return status;
        } catch (error) {
            if (isCurrent()) reportError(error);
        } finally {
            current.pending = false;
            if (isCurrent()) setPreparing(false);
        }
    }, [path]);

    useEffect(() => {
        setTanStatus(null);
        setPreparing(false);
        if (!enabled) {
            return;
        }
        const current = {active: true};
        lifecycle.current = current;
        refresh();
        return () => {
            current.active = false;
        };
    }, [enabled, refresh]);

    useEffect(() => {
        const expiresAt = Date.parse(tanStatus?.expiresAt);
        if (!enabled || submitting || !Number.isFinite(expiresAt)) return;

        const renewIfExpired = () => {
            if (document.visibilityState !== "hidden" && (tanStatus.expired || Date.now() >= expiresAt)) {
                refresh();
            }
        };
        // The server decides expiry. Avoid a request loop if the device clock is ahead.
        const remaining = expiresAt - Date.now();
        const timer = setTimeout(renewIfExpired, (remaining > 0 ? remaining : 60000));
        window.addEventListener("focus", renewIfExpired);
        document.addEventListener("visibilitychange", renewIfExpired);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("focus", renewIfExpired);
            document.removeEventListener("visibilitychange", renewIfExpired);
        };
    }, [enabled, submitting, tanStatus, refresh]);

    const requestNewTan = () => {
        if (!enabled || preparing || submitting) return;
        return refresh(true);
    };

    const handleTanError = async (error) => {
        if (getResponseStatus(error) !== 422 || getResponseBody(error)?.errorType !== "EXPIRED_RESENT") {
            return false;
        }
        setSignatureCode("");
        showMessage(expiredTanResentMessage);
        await refresh();
        return true;
    };

    return {signatureCode, setSignatureCode, tanStatus, tanBusy: preparing, requestNewTan, handleTanError};
};
